import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Grid, Stack, Typography, useTheme } from '@mui/material'
import { LockIcon } from 'lucide-react'

import { Card } from '@repo/ui'
import { EmptyState } from 'components'
import { InfoTooltip } from 'components/feedback/InfoTooltip'
import { ViewToggle, type ViewToggleOption } from 'components/input/ViewToggle/ViewToggle'
import { useAffiliatesDashboard, useInsidersFirstPromoter } from 'hooks/api/useInternalReferrals'
import { relativeOrDateTime } from 'utils'

import { toApiRange } from '../../affiliatesModel'
import { RANGE_PRESETS, rangeForPreset, type RangePreset } from '../../websiteAnalyticsModel'
import { AffiliatesHowItWorks } from './AffiliatesHowItWorks'
import {
  InsidersFirstPromoterCard,
  InsidersVeraCard,
  InsidersVeraSkeleton,
  ReferABrandSkeleton,
  ReferABrandTiles,
  ReferralFunnelCard,
  ReferralTrendCard,
  TopReferrersCard,
} from './AffiliatesCards'
import { ReferralsSwitchedOffNotice } from 'components/feedback/ReferralsSwitchedOffNotice'

const RANGE_OPTIONS: readonly ViewToggleOption<RangePreset>[] = RANGE_PRESETS.map((p) => ({
  value: p.value,
  label: p.label,
}))

const SectionTitle = ({ title, term }: { title: string; term: string }) => (
  <Stack direction="row" spacing={1.5} alignItems="center">
    <Typography variant="h6">{title}</Typography>
    <InfoTooltip term={term} />
  </Stack>
)

/**
 * VRG-665 — the Affiliates dashboard, GTM traction channel #14. Built as a tab of
 * `/internal/analytics`; VRG-678 moved it to `/internal/gtm/insiders`; VRG-684 renamed the page
 * Affiliates (`/internal/gtm/affiliates`) and split it into two tabs, so this panel renders ONE
 * programme per call: `section="refer"` (Refer & earn: brands referring brands) or
 * `section="insiders"` (Vera Insiders). Both carry the same How-it-works card and range toggle,
 * because the card covers both programmes and the range is one `?range=` param the tabs share.
 *
 * Two programmes, two sources, loaded independently so a slow FirstPromoter never holds
 * back vera-2's own figures:
 *   Refer a brand + Insider prospects — `GET /internal/referrals/dashboard` (vera-2's tables)
 *   FirstPromoter                     — `GET /internal/referrals/insiders/firstpromoter`
 *                                       (cached 15 minutes on the API; "Not connected" while
 *                                       the account's keys are unset — never zeros)
 *
 * The range is `?range=`, the same name and presets as Analytics' Website view. The staff table of
 * referrals (VRG-658, formerly People → Referrals) renders BELOW this panel on the Refer & earn
 * tab, as the same `ReferralsTab` component the People page used (§71) — not re-declared here.
 */
export const AffiliatesPanel = ({ section }: { section: 'refer' | 'insiders' }) => {
  const theme = useTheme()
  const [params, setParams] = useSearchParams()
  const preset: RangePreset = RANGE_PRESETS.some((p) => p.value === params.get('range'))
    ? (params.get('range') as RangePreset)
    : '30d'
  const range = useMemo(() => rangeForPreset(preset), [preset])
  const apiRange = useMemo(() => toApiRange(range), [range])
  const setPreset = (next: RangePreset) => {
    const p = new URLSearchParams(params)
    p.set('range', next)
    setParams(p, { replace: true })
  }

  const dashboard = useAffiliatesDashboard(apiRange)
  const fp = useInsidersFirstPromoter(apiRange, { enabled: section === 'insiders' })
  const data = dashboard.data
  const status = (dashboard.error as (Error & { status?: number }) | null)?.status

  return (
    // useFlexGap: a Stack's margin-based spacing overrides a Grid container's negative
    // margin, which pushed the tile grid 16px past the right gutter. The Website tab does the same.
    <Stack spacing={6} useFlexGap>
      <AffiliatesHowItWorks />
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ md: 'center' }}
        spacing={3}
      >
        <Typography variant="body2" color="text.secondary">
          {range.since} to {range.until} (UTC)
        </Typography>
        <ViewToggle options={RANGE_OPTIONS} value={preset} onChange={setPreset} ariaLabel="Date range" />
      </Stack>

      {section === 'refer' && <SectionTitle title="Refer & earn" term="refer-a-brand" />}
      {/* VRG-680 — only the Refer & earn half: Vera Insiders is not behind the switch. */}
      {section === 'refer' && <ReferralsSwitchedOffNotice mb={0} />}
      {section !== 'refer' ? null : dashboard.isLoading ? (
        <ReferABrandSkeleton />
      ) : dashboard.error || !data ? (
        <Card>
          <EmptyState
            icon={<LockIcon size={28} strokeWidth={1} color={theme.palette.text.secondary} />}
            title={status === 403 ? 'You cannot view affiliate figures' : 'Could not load affiliate figures'}
            text={
              status === 403
                ? 'Affiliate figures need the "View GTM" permission and a platform admin or manager role.'
                : 'The request failed. Reload the page, or check the API is running.'
            }
            sx={{ py: 10 }}
          />
        </Card>
      ) : (
        <Stack spacing={6} useFlexGap>
          <Typography variant="body3" color="text.secondary">
            Computed {relativeOrDateTime(data.computed_at)} from vera-2&rsquo;s referral records and
            activation ledger.
          </Typography>
          <ReferABrandTiles data={data} />
          <Grid container spacing={4}>
            <Grid item xs={12} lg={6}>
              <ReferralFunnelCard data={data} />
            </Grid>
            <Grid item xs={12} lg={6}>
              <ReferralTrendCard data={data} />
            </Grid>
          </Grid>
          <TopReferrersCard data={data} />
        </Stack>
      )}

      {section === 'insiders' && (
      <>
      <SectionTitle title="Vera Insiders" term="vera-insiders" />
      {dashboard.error && (
        <Card>
          <EmptyState
            icon={<LockIcon size={28} strokeWidth={1} color={theme.palette.text.secondary} />}
            title={status === 403 ? 'You cannot view affiliate figures' : 'Could not load affiliate figures'}
            text={
              status === 403
                ? 'Affiliate figures need the "View GTM" permission and a platform admin or manager role.'
                : 'The request failed. Reload the page, or check the API is running.'
            }
            sx={{ py: 10 }}
          />
        </Card>
      )}
      <Grid container spacing={4}>
        <Grid item xs={12} lg={6}>
          {dashboard.isLoading ? (
            <InsidersVeraSkeleton />
          ) : data ? (
            <InsidersVeraCard data={data} />
          ) : null}
        </Grid>
        <Grid item xs={12} lg={6}>
          <InsidersFirstPromoterCard report={fp.data} isLoading={fp.isLoading} failed={!!fp.error} />
        </Grid>
      </Grid>
      </>
      )}
    </Stack>
  )
}
